/**
 * Hono Entity Controller.
 *
 * Preserves every branch: CASL checks, sensitive-field filtering, bulk ops,
 * workflow transitions, pagination, field-level filtering. Handlers are
 * Hono-native (`(c) => Promise<Response>`) — errors thrown propagate to the
 * central `app.onError(errorHandler)`.
 */

import type {
  EntityDefinition,
  ModuleContext,
  ActionDefinition,
  CollectionEntityDefinition,
  SingleEntityDefinition,
  WorkflowConfig
} from '@gzl10/nexus-sdk'
import { resolveLocalized } from '@gzl10/nexus-sdk'
import { ZodError, type ZodIssue } from 'zod'
import type { EntityController, EntityHandler, EntityService } from './types.js'
import type { NexusContext } from '../types.js'
import type { ValidationDetail } from '../../core/errors/app-error.js'
import { getSchemas } from '@kernel/runtime/validation/index.js'
import {
  excludeSensitiveFields,
  excludeSensitiveFieldsFromResult,
  filterByPermittedFields,
  filterResultByPermittedFields,
  validateWritePermissions,
  getCaslConditionsForQuery
} from '@kernel/runtime/helpers/index.js'

/** Converts ZodIssue[] to ValidationDetail[] */
function zodToValidationDetails(issues: ZodIssue[]): ValidationDetail[] {
  return issues.map(e => ({
    path: e.path.join('.'),
    message: e.message
  }))
}

/** Capitalize first letter (for inferring CASL subject from table name) */
function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Whether CASL debug logging is enabled.
 * Set NEXUS_CASL_DEBUG=true for detailed permit/deny logs.
 */
const CASL_DEBUG = process.env['NEXUS_CASL_DEBUG'] === 'true'

/** Get all field names from entity definition */
function getAllFieldNames(definition: EntityDefinition): string[] {
  if (!('fields' in definition)) return []
  return Object.keys(definition.fields ?? {})
}

/** Safely extract CASL subject from definition.casl */
function getCaslSubject(casl: EntityDefinition['casl']): string | undefined {
  if (!casl) return undefined
  return 'subject' in casl ? casl.subject : undefined
}

/** Safely extract sensitiveFields from definition.casl */
function getCaslSensitiveFields(casl: EntityDefinition['casl']): string[] {
  if (!casl) return []
  return 'sensitiveFields' in casl ? (casl.sensitiveFields ?? []) : []
}

/** Query params reserved for pagination/sorting — never treated as field filters */
const RESERVED_QUERY_PARAMS = new Set(['page', 'limit', 'sort', 'order', 'search', 'filters', 'locale'])

/** Max items per bulk operation */
const MAX_BULK_SIZE = 100

/**
 * Execute a bulk operation with uniform error handling.
 */
async function executeBulkOperation<TResult, TItem = unknown>(
  items: unknown,
  options: {
    errors: ModuleContext['core']['errors']
    itemLabel?: string
    process: (item: TItem, index: number) => Promise<TResult>
  }
): Promise<{ results: TResult[]; errors: Array<{ index: number; id?: string; error: string }> }> {
  if (!Array.isArray(items)) {
    throw new options.errors.ValidationError(`Body must be an array${options.itemLabel ? ` of ${options.itemLabel}` : ''}`)
  }
  if (items.length === 0) {
    throw new options.errors.ValidationError('Array must not be empty')
  }
  if (items.length > MAX_BULK_SIZE) {
    throw new options.errors.ValidationError(`Max ${MAX_BULK_SIZE} items per bulk operation`)
  }

  const results: TResult[] = []
  const errors: Array<{ index: number; id?: string; error: string }> = []

  for (let i = 0; i < items.length; i++) {
    try {
      const result = await options.process(items[i] as TItem, i)
      results.push(result)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      const item = items[i]
      const id = typeof item === 'string' ? item : (item as Record<string, unknown>)?.['id'] as string | undefined
      errors.push({ index: i, ...(id ? { id } : {}), error: message })
    }
  }

  return { results, errors }
}

/**
 * Parse and validate filters from query string.
 * @throws ValidationError if filters are invalid
 */
function parseFilters(raw: string, errors: ModuleContext['core']['errors']): Record<string, unknown> {
  if (raw.length > 2048) {
    throw new errors.ValidationError('Filters too large (max 2KB)')
  }
  let filters: Record<string, unknown>
  try {
    filters = JSON.parse(raw)
  } catch {
    throw new errors.ValidationError('Invalid filters JSON')
  }
  if (typeof filters !== 'object' || Array.isArray(filters) || filters === null) {
    throw new errors.ValidationError('Filters must be a JSON object')
  }
  const keys = Object.keys(filters)
  if (keys.length > 20) {
    throw new errors.ValidationError('Too many filter clauses (max 20)')
  }
  for (const key of keys) {
    if (!/^[a-zA-Z_][a-zA-Z0-9_.]*$/.test(key)) {
      throw new errors.ValidationError(`Invalid filter key: ${key}`)
    }
  }
  return filters
}

/** Safely parse JSON body, returning empty object on failure/empty body.
 * Propagates SyntaxError for malformed JSON so the error handler can return 400. */
async function readJsonBody(c: NexusContext): Promise<Record<string, unknown>> {
  if (!c.req.header('content-type')?.includes('application/json')) return {}
  let text: string
  try {
    text = await c.req.text()
  } catch {
    return {}
  }
  if (!text.trim()) return {}
  // JSON.parse throws SyntaxError for malformed input — let it propagate.
  const body = JSON.parse(text) as unknown
  if (body && typeof body === 'object') return body as Record<string, unknown>
  return {}
}

/**
 * Create controller for an entity service with optional CASL authorization
 */
export function createEntityController(
  service: EntityService,
  definition: EntityDefinition,
  ctx: ModuleContext
): EntityController {
  const type = definition.type ?? 'collection'

  const entityName = 'table' in definition ? definition.table : ('key' in definition ? definition.key : 'entity')
  const caslSubject = getCaslSubject(definition.casl) ?? capitalizeFirst(entityName ?? 'Entity')
  const hasCasl = !!definition.casl
  const sensitiveFields = getCaslSensitiveFields(definition.casl)
  const allFields = getAllFieldNames(definition)

  if (!hasCasl && ['collection', 'single', 'tree', 'dag', 'reference', 'config', 'event'].includes(type)) {
    ctx.core.logger.warn(
      { entity: entityName, type },
      `Entity "${entityName}" has no CASL config — authenticated users have full access (no role-based restrictions)`
    )
  }

  const schemas = getSchemas(definition)

  /**
   * Verify CASL permission and log the authorization decision.
   *
   * @throws UnauthorizedError if not authenticated
   * @throws ForbiddenError if permission denied
   */
  function checkPermission(
    c: NexusContext,
    action: 'read' | 'create' | 'update' | 'delete' | 'manage' | 'execute',
    instance?: unknown
  ): void {
    const user = c.get('user')
    const ability = c.get('ability')
    const userId = user?.id ?? null
    const reqId = c.get('requestId')

    if (hasCasl) {
      if (!ability) {
        ctx.core.logger.warn({ action, subject: caslSubject, reqId, decision: 'deny', reason: 'unauthenticated' }, 'authz:deny')
        throw new ctx.core.errors.UnauthorizedError('Authentication required')
      }

      const { subject, ForbiddenError: CASLForbiddenError } = ctx.core.abilities
      const target = instance ? subject(caslSubject, instance as Record<string, unknown>) : caslSubject

      try {
        CASLForbiddenError.from(ability).throwUnlessCan(action, target)
      } catch {
        ctx.core.logger.warn({ action, subject: caslSubject, userId, reqId, decision: 'deny', reason: 'forbidden' }, 'authz:deny')
        throw new ctx.core.errors.ForbiddenError(`Not allowed to ${action} ${resolveLocalized(definition.label, 'en')}`)
      }

      if (CASL_DEBUG) {
        ctx.core.logger.debug({ action, subject: caslSubject, userId, reqId, decision: 'permit' }, 'authz:permit')
      }
      return
    }

    // Default-deny: no CASL → require authentication
    if (!ability) {
      ctx.core.logger.warn({ action, subject: caslSubject, reqId, decision: 'deny', reason: 'unauthenticated' }, 'authz:deny')
      throw new ctx.core.errors.UnauthorizedError('Authentication required')
    }

    if (CASL_DEBUG) {
      ctx.core.logger.debug({ action, subject: caslSubject, userId, reqId, decision: 'permit', reason: 'authenticated-no-rbac' }, 'authz:permit')
    }
  }

  const controller: EntityController = {
    async list(c: NexusContext): Promise<Response> {
      checkPermission(c, 'read')

      const page = Math.max(1, parseInt(c.req.query('page') ?? '') || 1)
      const rawLimit = parseInt(c.req.query('limit') ?? '')
      const limit = rawLimit === 0 ? 0 : Math.min(100, Math.max(1, rawLimit || 20))

      let filters: Record<string, unknown> | undefined
      const rawFilters = c.req.query('filters')
      if (rawFilters) {
        filters = parseFilters(rawFilters, ctx.core.errors)
      }

      const allQuery = c.req.query()
      for (const key of allFields) {
        if (key in allQuery && !RESERVED_QUERY_PARAMS.has(key)) {
          filters = filters ?? {}
          filters[key] = allQuery[key]
        }
      }

      const rawSort = c.req.query('sort')
      const explicitOrder = c.req.query('order') as 'asc' | 'desc' | undefined
      const sortDesc = rawSort?.startsWith('-')
      const sortRaw = sortDesc ? rawSort!.slice(1) : rawSort
      const sort = sortRaw?.replace(/([A-Z])/g, '_$1').toLowerCase()
      const order = explicitOrder ?? (sortDesc ? 'desc' : 'asc')

      const searchRaw = c.req.query('search')
      const query: import('./types.js').EntityQuery = {
        page,
        limit,
        sort,
        order,
        search: searchRaw ? String(searchRaw).slice(0, 200) : undefined,
        filters
      }

      const ability = c.get('ability')
      if (hasCasl && ability) {
        const caslConditions = getCaslConditionsForQuery(ability, 'read', caslSubject)
        if (caslConditions) {
          query.filters = { ...query.filters, ...caslConditions }
        }
      }

      const result = await service.findAll(query)

      let filtered = excludeSensitiveFieldsFromResult(
        result as { items: Record<string, unknown>[]; total: number; page: number; limit: number; totalPages: number; hasNext: boolean },
        sensitiveFields
      )
      filtered = filterResultByPermittedFields(
        filtered as { items: Record<string, unknown>[]; total: number; page: number; limit: number; totalPages: number; hasNext: boolean },
        ability,
        caslSubject,
        allFields
      )
      return c.json(filtered)
    },

    async get(c: NexusContext): Promise<Response> {
      const id = String(c.req.param('id') ?? '')

      const entity = await service.findById(id)

      if (!entity) {
        throw new ctx.core.errors.NotFoundError(resolveLocalized(definition.label, 'en'))
      }

      checkPermission(c, 'read', entity)

      const ability = c.get('ability')
      let filtered = excludeSensitiveFields(entity as Record<string, unknown>, sensitiveFields)
      filtered = filterByPermittedFields(
        filtered as Record<string, unknown>,
        ability,
        'read',
        caslSubject,
        allFields
      )
      return c.json(filtered)
    }
  }

  // Create
  const allowCreate = 'allowCreate' in definition ? definition.allowCreate !== false : true
  if (supportsCreate(type) && allowCreate) {
    controller.create = async (c: NexusContext): Promise<Response> => {
      checkPermission(c, 'create')

      if (!service.create) {
        throw new ctx.core.errors.ForbiddenError(`${resolveLocalized(definition.label, 'en')} does not support create`)
      }

      const body = await readJsonBody(c)

      let validated: Record<string, unknown>
      try {
        validated = schemas.create.parse(body)
      } catch (error) {
        if (error instanceof ZodError) {
          throw new ctx.core.errors.ValidationError('Validation failed', zodToValidationDetails(error.errors))
        }
        throw error
      }

      const ability = c.get('ability')
      const user = c.get('user')
      validateWritePermissions(validated, ability, 'create', caslSubject, allFields, ctx)

      if (user?.id) {
        validated['_authUserId'] = user.id
      }

      const entity = await service.create(validated)

      let filtered = excludeSensitiveFields(entity as Record<string, unknown>, sensitiveFields)
      filtered = filterByPermittedFields(filtered as Record<string, unknown>, ability, 'read', caslSubject, allFields)
      return c.json(filtered, 201)
    }
  }

  // Update
  const allowEdit = 'allowEdit' in definition ? definition.allowEdit !== false : true
  if (supportsUpdate(type) && allowEdit) {
    controller.update = async (c: NexusContext): Promise<Response> => {
      if (!service.update) {
        throw new ctx.core.errors.ForbiddenError(`${resolveLocalized(definition.label, 'en')} does not support update`)
      }

      const id = String(c.req.param('id') ?? '')

      const existing = await service.findById(id)
      if (!existing) {
        throw new ctx.core.errors.NotFoundError(resolveLocalized(definition.label, 'en'))
      }

      checkPermission(c, 'update', existing)

      const body = await readJsonBody(c)

      let validated: Record<string, unknown>
      try {
        validated = schemas.update.parse(body)
      } catch (error) {
        if (error instanceof ZodError) {
          throw new ctx.core.errors.ValidationError('Validation failed', zodToValidationDetails(error.errors))
        }
        throw error
      }

      const ability = c.get('ability')
      const user = c.get('user')
      validateWritePermissions(validated, ability, 'update', caslSubject, allFields, ctx)

      // Workflow transition CASL check
      const wf = (definition as { workflow?: WorkflowConfig }).workflow
      if (wf) {
        const field = wf.field ?? 'status'
        const newValue = validated[field] as string | undefined
        const currentValue = (existing as Record<string, unknown>)[field] as string
        if (newValue !== undefined && newValue !== currentValue) {
          const transition = wf.transitions.find(t => t.from === currentValue && t.to === newValue)
          if (transition?.casl?.action) {
            checkPermission(c, transition.casl.action as 'execute', existing)
          }
        }
      }

      if (user?.id) {
        validated['_authUserId'] = user.id
      }

      const entity = await service.update(id, validated)

      let filtered = excludeSensitiveFields(entity as Record<string, unknown>, sensitiveFields)
      filtered = filterByPermittedFields(filtered as Record<string, unknown>, ability, 'read', caslSubject, allFields)
      return c.json(filtered)
    }
  }

  // Delete
  const allowDelete = 'allowDelete' in definition ? definition.allowDelete !== false : true
  if (supportsDelete(type) && allowDelete) {
    controller.delete = async (c: NexusContext): Promise<Response> => {
      if (!service.delete) {
        throw new ctx.core.errors.ForbiddenError(`${resolveLocalized(definition.label, 'en')} does not support delete`)
      }

      const id = String(c.req.param('id') ?? '')

      const existing = await service.findById(id)
      if (!existing) {
        throw new ctx.core.errors.NotFoundError(resolveLocalized(definition.label, 'en'))
      }

      checkPermission(c, 'delete', existing)

      const user = c.get('user')
      await service.delete(id, { actorId: user?.id })
      return c.body(null, 204)
    }
  }

  // Count
  const isSingleton = (type === 'single' || type === 'config') && !('scopeField' in definition && (definition as SingleEntityDefinition).scopeField)
  const serviceCount = service.count?.bind(service)
  if (!isSingleton && serviceCount) {
    controller.count = async (c: NexusContext): Promise<Response> => {
      checkPermission(c, 'read')

      let filters: Record<string, unknown> | undefined
      const rawFilters = c.req.query('filters')
      if (rawFilters) {
        filters = parseFilters(rawFilters, ctx.core.errors)
      }

      const allQuery = c.req.query()
      for (const key of allFields) {
        if (key in allQuery && !RESERVED_QUERY_PARAMS.has(key)) {
          filters = filters ?? {}
          filters[key] = allQuery[key]
        }
      }

      const ability = c.get('ability')
      if (hasCasl && ability) {
        const caslConditions = getCaslConditionsForQuery(ability, 'read', caslSubject)
        if (caslConditions) {
          filters = { ...filters, ...caslConditions }
        }
      }

      const count = await serviceCount(filters)
      return c.json({ count })
    }
  }

  // Bulk Create
  if (supportsCreate(type) && allowCreate && service.create) {
    controller.bulkCreate = async (c: NexusContext): Promise<Response> => {
      checkPermission(c, 'create')
      const ability = c.get('ability')
      const user = c.get('user')
      const body = await readJsonBody(c)
      const items: unknown = Array.isArray(body) ? body : (body as unknown)

      const { results: created, errors } = await executeBulkOperation<unknown>(items, {
        errors: ctx.core.errors,
        process: async (item) => {
          let validated: Record<string, unknown>
          try {
            validated = schemas.create.parse(item)
          } catch (error) {
            if (error instanceof ZodError) {
              throw new Error(error.errors.map(e => e.message).join(', '))
            }
            throw error
          }

          validateWritePermissions(validated, ability, 'create', caslSubject, allFields, ctx)

          if (user?.id) {
            validated['_authUserId'] = user.id
          }

          const entity = await service.create!(validated)
          const filtered = excludeSensitiveFields(entity as Record<string, unknown>, sensitiveFields)
          return filterByPermittedFields(filtered as Record<string, unknown>, ability, 'read', caslSubject, allFields)
        }
      })

      return c.json({ created, errors }, errors.length === 0 ? 201 : 207)
    }
  }

  // Bulk Update
  if (supportsUpdate(type) && allowEdit && service.update) {
    controller.bulkUpdate = async (c: NexusContext): Promise<Response> => {
      const ability = c.get('ability')
      const user = c.get('user')
      const body = await readJsonBody(c)
      const items: unknown = Array.isArray(body) ? body : (body as unknown)

      const { results: updated, errors } = await executeBulkOperation<unknown, { id: string; data: Record<string, unknown> }>(items, {
        errors: ctx.core.errors,
        itemLabel: '{ id, data }',
        process: async (item) => {
          if (!item?.id || !item?.data) {
            throw new Error('Each item must have id and data')
          }

          const existing = await service.findById(item.id)
          if (!existing) {
            throw new Error('Not found')
          }

          checkPermission(c, 'update', existing)

          let validated: Record<string, unknown>
          try {
            validated = schemas.update.parse(item.data)
          } catch (error) {
            if (error instanceof ZodError) {
              throw new Error(error.errors.map(e => e.message).join(', '))
            }
            throw error
          }

          validateWritePermissions(validated, ability, 'update', caslSubject, allFields, ctx)

          if (user?.id) {
            validated['_authUserId'] = user.id
          }

          const entity = await service.update!(item.id, validated)
          const filtered = excludeSensitiveFields(entity as Record<string, unknown>, sensitiveFields)
          return filterByPermittedFields(filtered as Record<string, unknown>, ability, 'read', caslSubject, allFields)
        }
      })

      return c.json({ updated, errors }, errors.length === 0 ? 200 : 207)
    }
  }

  // Bulk Delete
  if (supportsDelete(type) && allowDelete && service.delete) {
    controller.bulkDelete = async (c: NexusContext): Promise<Response> => {
      const body = await readJsonBody(c)
      if (!body || !Array.isArray(body['ids'])) {
        throw new ctx.core.errors.ValidationError('Body must have { ids: string[] }')
      }
      if (body['ids'].length === 0) {
        throw new ctx.core.errors.ValidationError('IDs array must not be empty')
      }
      const ids = body['ids'] as string[]

      const { results: deleted, errors } = await executeBulkOperation<string>(ids, {
        errors: ctx.core.errors,
        process: async (id) => {
          const existing = await service.findById(id as string)
          if (!existing) {
            throw new Error('Not found')
          }

          checkPermission(c, 'delete', existing)
          await service.delete!(id as string)
          return id as string
        }
      })

      return c.json({ deleted, errors }, errors.length === 0 ? 200 : 207)
    }
  }

  // Recompute (computed entities only)
  const serviceAny = service as unknown as Record<string, unknown>
  const serviceRecompute = serviceAny['recompute'] as
    ((params?: Record<string, unknown>) => Promise<unknown[]>) | undefined
  if (type === 'computed' && typeof serviceRecompute === 'function') {
    controller.recompute = async (c: NexusContext): Promise<Response> => {
      checkPermission(c, 'read')
      const items = await serviceRecompute()
      return c.json({ recomputed: items.length })
    }
  }

  return controller
}

function supportsCreate(type: string): boolean {
  return ['collection', 'external', 'temp', 'reference', 'config', 'tree', 'dag'].includes(type)
}

function supportsUpdate(type: string): boolean {
  return ['collection', 'external', 'single', 'temp', 'reference', 'config', 'tree', 'dag'].includes(type)
}

function supportsDelete(type: string): boolean {
  return ['collection', 'external', 'temp', 'reference', 'tree', 'dag'].includes(type)
}

/**
 * Extra input that callers (e.g. SSE batch route) can inject into an action's
 * extendedInput before `handler` is invoked. Read via `c.get('_actionExtraInput')`.
 */
export const ACTION_EXTRA_INPUT_KEY = '_actionExtraInput' as const

/**
 * Create handler for an ActionDefinition defined in a collection/single entity.
 *
 * TODO: `action.handler` signature still takes Express `req, res` (SDK). Until
 * the SDK is updated to a context-based shape, we pass `undefined` for req/res.
 * Handlers that currently touch req/res (cookies, multer, raw streaming) will
 * need to be migrated alongside the SDK change in a follow-up phase.
 */
export function createActionHandler(
  action: ActionDefinition,
  definition: CollectionEntityDefinition | SingleEntityDefinition,
  ctx: ModuleContext,
  scope: 'row' | 'entity' = 'row'
): EntityHandler {
  const tableName = 'table' in definition ? definition.table : undefined

  const entityName = 'table' in definition ? definition.table : ('key' in definition ? definition.key : 'entity')
  const caslSubject = (action.casl && 'subject' in action.casl ? action.casl.subject : undefined)
    ?? definition.casl?.subject
    ?? capitalizeFirst(entityName ?? 'Entity')
  const hasCasl = !action.skipAuth && (!!definition.casl || !!action.casl)

  return async (c: NexusContext): Promise<Response> => {
    const recordId = scope === 'row' ? String(c.req.param('id') ?? '') : undefined
    const user = c.get('user')
    const ability = c.get('ability')
    const requestId = c.get('requestId')

    if (hasCasl && !ability) {
      ctx.core.logger.warn({ action: 'execute', subject: caslSubject, actionKey: action.key, reqId: requestId, decision: 'deny', reason: 'unauthenticated' }, 'authz:deny')
      throw new ctx.core.errors.UnauthorizedError('Authentication required')
    }

    let record: Record<string, unknown> | undefined
    if (scope === 'row' && tableName && recordId) {
      let query = ctx.db.knex(tableName).where('id', recordId)
      if (action.select?.length) {
        query = query.select(action.select)
      }
      record = await query.first()

      if (!record) {
        throw new ctx.core.errors.NotFoundError(resolveLocalized(definition.label, 'en'))
      }
    }

    if (hasCasl && ability) {
      const { subject, ForbiddenError: CASLForbiddenError } = ctx.core.abilities
      const caslAction = (action.casl && 'action' in action.casl) ? action.casl.action ?? 'execute' : 'execute'
      const target = record ? subject(caslSubject, record) : caslSubject

      try {
        CASLForbiddenError.from(ability).throwUnlessCan(caslAction, target)
      } catch {
        ctx.core.logger.warn({ action: caslAction, subject: caslSubject, actionKey: action.key, userId: user?.id, reqId: requestId, decision: 'deny', reason: 'forbidden' }, 'authz:deny')
        throw new ctx.core.errors.ForbiddenError(`Not allowed to execute ${resolveLocalized(action.label, 'en')}`)
      }

      if (CASL_DEBUG) {
        ctx.core.logger.debug({ action: caslAction, subject: caslSubject, actionKey: action.key, userId: user?.id, reqId: requestId, decision: 'permit' }, 'authz:permit')
      }
    }

    // Validate input — merge body+query for GET-style actions and SSE batch stream (which uses GET w/ query params)
    const bodyRaw = await readJsonBody(c)
    const queryAll = c.req.query()
    let input: Record<string, unknown> = { ...queryAll, ...bodyRaw }
    if (action.inputSchema) {
      try {
        input = action.inputSchema.parse(input) as Record<string, unknown>
      } catch (error) {
        if (error instanceof ZodError) {
          throw new ctx.core.errors.ValidationError('Validation failed', zodToValidationDetails(error.errors))
        }
        throw error
      }
    }

    const extendedInput: Record<string, unknown> = { ...input }
    if (record) {
      extendedInput['_record'] = record
    }
    if (user?.id) {
      extendedInput['_authUserId'] = user.id
    }

    // Allow SSE batch route to inject _batchReporter and other extras
    // TODO: c.var typing for custom keys — using (c as any).get to avoid strict NexusEnv.Variables typing
    const extra = (c as unknown as { get: (k: string) => Record<string, unknown> | undefined }).get(ACTION_EXTRA_INPUT_KEY)
    if (extra) {
      Object.assign(extendedInput, extra)
    }

    if (!action.handler) {
      throw new ctx.core.errors.AppError(`No handler defined for action ${action.key}`, 500)
    }

    // SDK action handler signature: (ctx, input, c?: Context). Pass Hono context
    // so action handlers can stream, set headers, parse body, etc. (Phase D+).
    const result = await action.handler(ctx, extendedInput, c)

    if (result instanceof Response) {
      return result
    }
    if (result !== undefined) {
      return c.json(result as Record<string, unknown> | unknown[])
    }
    return c.body(null, 200)
  }
}
